import { styled } from "goober";
import { useRef } from "react";
import { useChatWorkflowUrl } from "../../lib/chatWorkflowUrl";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useNavTransition } from "../../hooks/useNavTransition";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useScrollFade } from "../../hooks/useScrollFade";
import { navigate, routes, useMakeUrl } from "../../lib/urls";
import { Button } from "../Button";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import {
  Container,
  Heading,
  SpaceItemContainer,
  StyledLink,
} from "../Navigation";
import { BadgeDot } from "../BadgeDot";
import { StatusIndicator } from "../StatusIndicator";
import { NavigationStatus } from "../NavigationStatus";
import { SubtleButton, SubtleButtonLink } from "../SubtleButton";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { chatActionGroups } from "./ChatActionsMenu";
import {
  ChatRowBadge,
  type ChatRowData,
  ChatRowSubtitle,
} from "./ChatRowParts";
import { chatLabel } from "./pickChatIconColor";

type Chat = ChatRowData & {
  pinned?: boolean;
  started?: boolean;
  responding?: boolean;
  awaitingAction?: boolean;
};

type Props = {
  chats: readonly Chat[];
  activeChatId: string | null;
  userId?: string;
  onDelete?: ((id: string) => void) | undefined;
  onRename?: ((id: string) => void) | undefined;
  onFork?: ((id: string) => void) | undefined;
  onDownload?: ((id: string) => void) | undefined;
  onTogglePin?: ((id: string) => void) | undefined;
  onNewChat?: (() => void) | undefined;
  onRecentlyDeleted?: (() => void) | undefined;
  onTidyUp?: (() => void) | undefined;
  readOnly?: boolean;
};

const ChatItemContainer = styled(SpaceItemContainer)`
  &:hover > a,
  &:has([data-dropdown-open="true"]) > a {
    background-color: var(--alpha4);
  }
  &:has([data-dropdown-open="true"]) [data-space-action] {
    opacity: 1;
    pointer-events: auto;
  }
  &:has([data-dropdown-open="true"]) [data-hide-on-hover] {
    opacity: 0;
  }
  &:hover > a[data-action="true"],
  &:focus-within > a[data-action="true"],
  &:has([data-dropdown-open="true"]) > a {
    padding-right: 34px;
  }
`;

// Room for the row’s action button is made only while that button can show.
const ChatLink = styled(StyledLink)`
  flex: 1;
  min-width: 0;
  &[data-status="true"],
  &[data-mobile="true"][data-action="true"] {
    padding-right: 34px;
  }
  &[aria-current="page"] {
    background: var(--alpha4);
  }
`;

const RowText = styled("span")`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ChatHeading = styled(Heading)`
  min-height: 44px;
`;

export function PersonalChatNavigation({
  chats,
  activeChatId,
  userId,
  onDelete,
  onRename,
  onFork,
  onDownload,
  onTogglePin,
  onNewChat,
  onRecentlyDeleted,
  onTidyUp,
  readOnly,
}: Props) {
  const makeUrl = useMakeUrl();
  const chatWorkflowUrl = useChatWorkflowUrl();
  const navEnter = useNavTransition("chat");
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef });
  const isMobile = useIsMobile();
  const [personalSpace] = useQuery(readOnly ? null : queries.personalSpace());
  const SectionHeading = readOnly ? Heading : ChatHeading;
  const pinned = chats.filter((chat) => chat.pinned);
  const recent = chats
    .filter((chat) => !chat.pinned)
    .toSorted((a, b) => {
      if (a.pending !== b.pending) return a.pending ? -1 : 1;
      return (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0);
    });
  const actionSections = (chat: Chat) => {
    const { primary, secondary } = chatActionGroups({
      pinned: chat.pinned,
      onRename: !chat.pending && onRename ? () => onRename(chat.id) : undefined,
      onTogglePin:
        !chat.pending && onTogglePin ? () => onTogglePin(chat.id) : undefined,
      onFork: chat.started && onFork ? () => onFork(chat.id) : undefined,
      onDownload:
        chat.started && onDownload ? () => onDownload(chat.id) : undefined,
      onDelete: onDelete ? () => onDelete(chat.id) : undefined,
    });
    return [primary, secondary].map((group) => ({
      items: group.map((action) => ({
        type: "button" as const,
        left: <Icon name={action.icon} size={16} />,
        label: action.label,
        onClick: action.onClick,
      })),
    }));
  };
  const menuItems = [
    ...(personalSpace
      ? [
          {
            type: "button" as const,
            left: <Icon name="box" size={16} />,
            label: "Browse storage",
            onClick: () =>
              navigate(routes.modal.storage, {
                spaceId: personalSpace.id,
                section: "chats",
              }),
          },
        ]
      : []),
    ...(onRecentlyDeleted
      ? [
          {
            type: "button" as const,
            left: <Icon name="package-2" size={16} />,
            label: "Deleted chats",
            onClick: onRecentlyDeleted,
          },
        ]
      : []),
  ];

  const chatHref = (chat: Chat) =>
    chat.workflow && !readOnly
      ? chatWorkflowUrl(chat.id, chat.workflow)
      : makeUrl(routes.chats, { tabId: chat.id, userId });
  const workflowNavigationHref = (chat: Chat) =>
    `${chatHref(chat)}&navigation=workflow`;
  const hasAction = (chat: Chat) =>
    chat.workflow
      ? !readOnly
      : actionSections(chat).some((section) => section.items.length > 0);
  const showsStatus = (chat: Chat) =>
    chat.responding ? chat.id !== activeChatId : !!chat.awaitingAction;
  const renderChat = (chat: Chat) => (
    <ChatItemContainer key={chat.id}>
      <ChatLink
        to={chatHref(chat)}
        data-action={hasAction(chat)}
        data-status={showsStatus(chat)}
        data-mobile={isMobile}
        aria-current={chat.id === activeChatId ? "page" : undefined}
        onDoubleClick={
          chat.workflow && !readOnly
            ? () =>
                history.pushState(
                  history.state,
                  "",
                  workflowNavigationHref(chat)
                )
            : undefined
        }
        title={chat.workflow?.name ?? chatLabel(chat.name)}
        aria-label={chat.workflow?.name ?? chatLabel(chat.name)}
      >
        <ChatRowBadge chat={chat} />
        <RowText>
          <Text ellipsis>{chat.workflow?.name ?? chatLabel(chat.name)}</Text>
          <ChatRowSubtitle chat={chat} />
        </RowText>
        {showsStatus(chat) && (
          <NavigationStatus
            aria-hidden="true"
            data-hide-on-hover=""
            data-mobile={isMobile}
          >
            {chat.responding ? (
              <StatusIndicator state="building" focused={false} size={12} />
            ) : (
              <BadgeDot />
            )}
          </NavigationStatus>
        )}
      </ChatLink>
      {chat.workflow && !readOnly && (
        <div
          data-space-action=""
          style={{
            position: "absolute",
            right: 4,
            ...(isMobile ? { opacity: 1, pointerEvents: "auto" } : {}),
          }}
        >
          <SubtleButtonLink
            to={workflowNavigationHref(chat)}
            icon="chevron-right"
            variant="secondary"
            size="m"
            aria-label={`${chat.workflow.name} versions and branches`}
          />
        </div>
      )}
      {!chat.workflow &&
        actionSections(chat).some((section) => section.items.length > 0) && (
          <div
            data-space-action=""
            style={{
              position: "absolute",
              right: 4,
              ...(isMobile ? { opacity: 1, pointerEvents: "auto" } : {}),
            }}
          >
            <Dropdown
              opener={
                <SubtleButton
                  icon="more"
                  variant="secondary"
                  aria-label="Chat actions"
                />
              }
              align="right"
              margin="0 6px"
              minWidth={180}
              sections={actionSections(chat)}
            />
          </div>
        )}
    </ChatItemContainer>
  );

  return (
    <Container
      ref={scrollRef}
      role="navigation"
      aria-label="Personal chats"
      data-nav-enter={navEnter}
    >
      {onNewChat && (
        <Button
          variant="subtle"
          size="l"
          icon="plus"
          style={{
            width: "100%",
            justifyContent: "center",
            flexShrink: 0,
            marginBottom: 6,
          }}
          onClick={onNewChat}
          aria-keyshortcuts="Alt+Shift+N"
          data-navigation-close
        >
          New chat
        </Button>
      )}
      {pinned.length > 0 && (
        <SectionHeading>
          <Text variant="text-small">Pinned</Text>
        </SectionHeading>
      )}
      {pinned.map(renderChat)}
      <SectionHeading style={{ marginTop: pinned.length > 0 ? 8 : 0 }}>
        <Text variant="text-small">Recent chats</Text>
        <div style={{ display: "flex", gap: 2 }}>
          {onTidyUp && (
            <Tooltip content="Tidy up chats">
              <SubtleButton
                icon="brush-cleaning"
                variant="secondary"
                aria-label="Tidy up chats"
                onClick={onTidyUp}
              />
            </Tooltip>
          )}
          {menuItems.length > 0 && (
            <Dropdown
              aria-label="Chat options"
              opener={
                <SubtleButton
                  icon="more"
                  variant="secondary"
                  aria-label="Chat options"
                />
              }
              align="right"
              minWidth={180}
              sections={[{ items: menuItems }]}
            />
          )}
        </div>
      </SectionHeading>
      {recent.map(renderChat)}
    </Container>
  );
}
